/**
 * Wochenrückblick: die letzten sieben Tage auf einer Seite — Essen, Training,
 * Supplements, Gewicht. Jeweils gegen die sieben Tage davor, wo ein Vergleich
 * etwas sagt.
 *
 * Reine Funktion: App (Anzeige) und Scheduler (Mitteilung am Sonntag) rechnen
 * mit derselben. Fehlende Daten bleiben `null` — eine Woche ohne Protokoll hat
 * keinen Proteinschnitt von 0, sondern keinen.
 */

import { weightTrend } from './body.js';
import type { WeighIn, WeightTrend } from './body.js';
import { addDays, isoDateInZone, wallTimeInZone } from './time.js';

export interface ReviewInput {
  /** Letzter Tag der Woche (heute), Ortszeit. */
  today: string;
  timeZone: string;
  now: Date;
  /**
   * `kcal` fehlt beim Scheduler (28.10.2026): Im verschlüsselten Konto liest
   * der Server nur das Eiweiß. Fehlt es an einem Eintrag der Woche, ist der
   * Kalorienschnitt `null` — nie eine Summe, die Unbekanntes als 0 zählt.
   */
  food: readonly {
    date: string;
    kcal?: number | null;
    protein: number;
    deletedAt?: string | null;
  }[];
  targets: { kcal: number; protein: number } | null;
  sessions: readonly { id: string; startedAt: string; deletedAt?: string | null }[];
  sets: readonly {
    sessionId: string;
    weightKg: number | null;
    reps: number | null;
    isWarmup: boolean;
    deletedAt?: string | null;
  }[];
  intakes: readonly {
    dueAt: string;
    acknowledgedAt: string | null;
    skippedAt: string | null;
    deletedAt?: string | null;
  }[];
  weights: readonly WeighIn[];
}

export interface WeekReview {
  from: string;
  to: string;
  nutrition: {
    /** Tage mit mindestens einem Eintrag. */
    daysLogged: number;
    /** Schnitt über die protokollierten Tage — `null` ohne Protokoll. */
    avgProtein: number | null;
    avgKcal: number | null;
    /** Tage, an denen das Proteinziel erreicht wurde (nur mit Ziel). */
    proteinGoalDays: number | null;
  };
  training: {
    sessions: number;
    previousSessions: number;
    /** Kilogramm × Wiederholungen der Arbeitssätze; Läufe und Planken zählen hier nicht. */
    volumeKg: number;
    previousVolumeKg: number;
  };
  supplements: {
    /** Fällige Einnahmen bis jetzt. */
    due: number;
    taken: number;
    skipped: number;
    /** Anteil genommen, 0–1; `null` ohne fällige Einnahmen. */
    rate: number | null;
  };
  weight: WeightTrend;
}

export function weekReview(input: ReviewInput): WeekReview {
  const to = input.today;
  const from = addDays(to, -6);
  const prevFrom = addDays(to, -13);
  const prevTo = addDays(to, -7);
  const inWeek = (date: string) => date >= from && date <= to;
  const inPrev = (date: string) => date >= prevFrom && date <= prevTo;

  // Essen: je Tag summieren, dann über die Tage mitteln.
  const perDay = new Map<string, { kcal: number; protein: number }>();
  let kcalKnown = true;
  for (const entry of input.food) {
    if (entry.deletedAt || !inWeek(entry.date)) continue;
    const day = perDay.get(entry.date) ?? { kcal: 0, protein: 0 };
    if (typeof entry.kcal === 'number') day.kcal += entry.kcal;
    else kcalKnown = false;
    day.protein += entry.protein;
    perDay.set(entry.date, day);
  }
  const days = [...perDay.values()];
  const avg = (pick: (day: { kcal: number; protein: number }) => number) =>
    days.length === 0
      ? null
      : Math.round(days.reduce((sum, day) => sum + pick(day), 0) / days.length);

  // Training: Tag der Einheit in Ortszeit.
  const dayOf = new Map<string, string>();
  let sessions = 0;
  let previousSessions = 0;
  for (const session of input.sessions) {
    if (session.deletedAt) continue;
    const day = isoDateInZone(new Date(session.startedAt), input.timeZone);
    dayOf.set(session.id, day);
    if (inWeek(day)) sessions += 1;
    else if (inPrev(day)) previousSessions += 1;
  }
  let volumeKg = 0;
  let previousVolumeKg = 0;
  for (const set of input.sets) {
    if (set.deletedAt || set.isWarmup || set.weightKg === null || set.reps === null) continue;
    const day = dayOf.get(set.sessionId);
    if (!day) continue;
    if (inWeek(day)) volumeKg += set.weightKg * set.reps;
    else if (inPrev(day)) previousVolumeKg += set.weightKg * set.reps;
  }

  // Supplements: nur, was schon fällig war — eine Einnahme heute Abend ist kein Versäumnis.
  let due = 0;
  let taken = 0;
  let skipped = 0;
  for (const intake of input.intakes) {
    if (intake.deletedAt) continue;
    const at = new Date(intake.dueAt);
    if (at > input.now || !inWeek(isoDateInZone(at, input.timeZone))) continue;
    due += 1;
    if (intake.acknowledgedAt) taken += 1;
    else if (intake.skippedAt) skipped += 1;
  }

  return {
    from,
    to,
    nutrition: {
      daysLogged: days.length,
      avgProtein: avg((day) => day.protein),
      avgKcal: kcalKnown ? avg((day) => day.kcal) : null,
      proteinGoalDays: input.targets
        ? days.filter((day) => day.protein >= input.targets!.protein).length
        : null,
    },
    training: {
      sessions,
      previousSessions,
      volumeKg: Math.round(volumeKg),
      previousVolumeKg: Math.round(previousVolumeKg),
    },
    supplements: {
      due,
      taken,
      skipped,
      rate: due === 0 ? null : Math.round((taken / due) * 100) / 100,
    },
    weight: weightTrend(input.weights, to),
  };
}

/** Ab wann am Sonntag der Rückblick kommt — Ortszeit. Die Woche ist dann fast vorbei. */
export const REVIEW_TIME = '18:00';

/**
 * Ist jetzt die Zeit für den Rückblick? Sonntag ab `REVIEW_TIME`, und nur,
 * wenn er für diesen Sonntag noch nicht ging.
 */
export function reviewDue(
  now: Date,
  timeZone: string,
  sentOn: string | null,
): { due: boolean; today: string } {
  const today = isoDateInZone(now, timeZone);
  const sunday = new Date(`${today}T12:00:00Z`).getUTCDay() === 0;
  return { due: sunday && wallTimeInZone(now, timeZone) >= REVIEW_TIME && sentOn !== today, today };
}

/** Text der Mitteilung — die wichtigsten Zahlen, ohne Urteil. Fehlendes bleibt weg. */
export function reviewPushText(review: WeekReview): { title: string; body: string } {
  const parts: string[] = [];
  if (review.nutrition.avgProtein !== null) {
    parts.push(`Ø ${review.nutrition.avgProtein} g Protein`);
  }
  parts.push(
    review.training.sessions === 1 ? '1 Einheit' : `${review.training.sessions} Einheiten`,
  );
  if (review.supplements.rate !== null) {
    parts.push(`Supplements ${Math.round(review.supplements.rate * 100)} %`);
  }
  if (review.weight.change !== null) {
    const sign = review.weight.change > 0 ? '+' : review.weight.change < 0 ? '−' : '±';
    parts.push(`Gewicht ${sign}${Math.abs(review.weight.change).toLocaleString('de-DE')} kg`);
  }
  return { title: 'Deine Woche', body: parts.join(' · ') };
}

export interface ReviewPoint {
  /** Letzter Tag der Woche. */
  to: string;
  weightKg: number | null;
  avgProtein: number | null;
  volumeKg: number;
  sessions: number;
  /** 0–1 */
  supplementRate: number | null;
}

/**
 * Verlauf über mehrere Wochen — **dieselbe** Rechnung wie der Wochenrückblick,
 * nur für frühere Wochen wiederholt. So zeigt die Linie nie etwas anderes als
 * die Wochenansicht. Älteste Woche zuerst; fehlende Werte bleiben `null` (eine
 * Lücke in der Linie, keine 0).
 */
export function reviewSeries(input: ReviewInput, weeks: number): ReviewPoint[] {
  const points: ReviewPoint[] = [];
  for (let back = weeks - 1; back >= 0; back -= 1) {
    const to = addDays(input.today, -7 * back);
    // „Jetzt" der früheren Woche ist ihr Ende — alles war dort schon fällig.
    const now =
      back === 0
        ? input.now
        : new Date(Math.min(input.now.getTime(), Date.parse(`${to}T23:59:59Z`)));
    const review = weekReview({ ...input, today: to, now });
    points.push({
      to,
      weightKg: review.weight.avg7,
      avgProtein: review.nutrition.avgProtein,
      volumeKg: review.training.volumeKg,
      sessions: review.training.sessions,
      supplementRate: review.supplements.rate,
    });
  }
  return points;
}
